import { useEffect, useMemo, useState } from "react";
import { api, type CalEvent, type CalendarData, type Horizon } from "../api.ts";
import { addDays, captionStyle, colors, dayLabel, daysBetween, serif, todayISO, tokens } from "../theme.ts";
import { BottomSheet } from "../components/BottomSheet.tsx";
import { DayList, buildDays, whenOf } from "../components/DayList.tsx";
import { GlassCard } from "../components/glass.tsx";

/** The residency deadline. The one countdown in the app, and plain date arithmetic. */
const DEADLINE = { iso: "2027-02-24", shown: "24.02.2027" };

const countdown = (today: string): string | null => {
  const n = daysBetween(today, DEADLINE.iso);
  if (n < 0) return null;
  if (n === 0) return `Today is ${DEADLINE.shown}`;
  return `${n} ${n === 1 ? "day" : "days"} to ${DEADLINE.shown}`;
};

/**
 * Calendar: the next 21 days, one card per day, empty days left out. Dated
 * tasks (from the horizon) and the feeds' events side by side; the feed is
 * read-only, so its sheet says where to change an event. The only countdown
 * in the app is the line at the top.
 */
export function Calendar({ version, onOpen }: { version: number; onOpen: (id: string) => void }) {
  const [horizon, setHorizon] = useState<Horizon | null>(null);
  const [cal, setCal] = useState<CalendarData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<CalEvent | null>(null);
  const today = todayISO();

  useEffect(() => {
    let live = true;
    Promise.all([api.horizon(21), api.calendar(today, addDays(today, 21))])
      .then(([h, c]) => { if (live) { setHorizon(h); setCal(c); setError(null); } })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [version, today]);

  const days = useMemo(
    () => (horizon && cal ? buildDays(horizon.dated, cal.events, cal.sources, today) : []),
    [horizon, cal, today],
  );
  const next = days.find((d) => d.date >= today);
  const nextItem = next?.items[0];
  const nextTitle = nextItem ? (nextItem.kind === "task" ? nextItem.task.title : nextItem.event.summary ?? "Untitled") : null;
  const line = countdown(today);

  return (
    <div style={{ padding: `12px ${tokens.space.gutter}px 0` }}>
      <div style={{
        marginBottom: 14, fontFamily: serif, fontSize: tokens.type.screenTitle.size,
        lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
      }}>Calendar</div>

      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {!horizon && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}

      {horizon && cal && (
        <>
          <GlassCard padding={16}>
            {next && nextTitle && (
              <div style={{ fontSize: 15, color: colors.ink }}>Next: {dayLabel(next.date)} · {nextTitle}</div>
            )}
            {line && <div style={{ fontSize: 15, color: colors.ink, marginTop: next && nextTitle ? 6 : 0 }}>{line}</div>}
            {!line && !(next && nextTitle) && <div style={{ fontSize: 15, color: colors.ink2 }}>Nothing dated.</div>}
          </GlassCard>

          {days.length === 0 && (
            <GlassCard padding={16} style={{ marginTop: 14 }}>
              <p style={{ margin: 0, fontSize: 15, color: colors.ink2 }}>Nothing dated in the next 21 days.</p>
            </GlassCard>
          )}

          <DayList days={days} today={today} onOpenTask={onOpen} onOpenEvent={setEvent} />
        </>
      )}

      {event && <EventSheet event={event} source={cal?.sources.find((s) => s.id === event.source)?.label ?? null} onClose={() => setEvent(null)} />}
    </div>
  );
}

/** A feed event. Read-only: the app never writes to a calendar. */
function EventSheet({ event, source, onClose }: { event: CalEvent; source: string | null; onClose: () => void }) {
  return (
    <BottomSheet onClose={onClose}>
      <div style={captionStyle}>{dayLabel(event.on_day)}{source ? ` · ${source}` : ""}</div>
      <div style={{
        fontFamily: serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight,
        margin: "6px 0 10px", color: colors.ink,
      }}>{event.summary ?? "Untitled"}</div>
      <p style={{ margin: "0 0 4px", fontSize: 15, color: colors.ink }}>{whenOf(event)}</p>
      {event.location && <p style={{ margin: "0 0 4px", fontSize: 14, color: colors.ink2 }}>{event.location}</p>}
      <p style={{ margin: "12px 0 0", fontSize: 13, color: colors.ink2 }}>Change it in Google Calendar.</p>
    </BottomSheet>
  );
}
